import { computed, inject } from '@angular/core';
import { isArray } from 'lodash';
import { tapResponse } from '@ngrx/operators';
import { patchState, signalStore, withComputed, withMethods, withState } from '@ngrx/signals';
import { removeEntity, setAllEntities, withEntities } from '@ngrx/signals/entities';
import {
  setError,
  setComplete,
  withRequestStatus,
  setPending,
} from './features/request-status.feature';
import { GeneralStore } from './general.store';
import { LocationService } from 'src/services/location.service';
import { ApiRequestParams } from 'src/models/global-interfaces';
import { components } from 'be-types';
import { LocalPermissions } from 'src/models/global-enum';

export interface LocationPreview {
  id: number;
  locationName: string;
  locationSchedulerActive: number;
  order: number;
  taskBoardUser: string;
  isDeployed: boolean;
}

export type LocationOrder = components['schemas']['LocationResource'];

export interface PreviousLocation {
  previousLocationId: number | null;
  previousLocationName: string;
  previousLocationUrl: string;
}
export type LocationMinified = components['schemas']['LocationsWithDeployedResource'];
export type LocationDefaultUserResource = components['schemas']['LocationDefaultUserResource'];
export type LocationDetails = components['schemas']['LocationShowResource'];
export type AssignedLocation = components['schemas']['Location'];
export type Timezone = components['schemas']['TimezoneResource'];

export interface LocationState {
  currentLocationId: number | null;
  previousLocationId: number | null;
  previousLocationName: string | null;
  previousLocationUrl: string | null;
  currentLocation: LocationPreview | null;
}

export const LocationStore = signalStore(
  { providedIn: 'root' },
  withEntities<LocationPreview>(),
  withState<LocationState>({
    currentLocationId: null,
    previousLocationId: null,
    previousLocationName: null,
    previousLocationUrl: null,
    currentLocation: null,
  }),
  withRequestStatus(),
  withComputed((store, generalStore = inject(GeneralStore)) => {
    const currentLocationPermissions = computed(() =>
      store.currentLocationId()
        ? (generalStore.locationCreds().get(store.currentLocationId()!) ?? [])
        : [],
    );
    return {
      currentLocationPermissions,
      isUserAllowedToAssignTasksAndSets: computed(
        () =>
          currentLocationPermissions().includes(LocalPermissions.ASSIGN_TASK_AND_SETS_TO_OTHERS) ||
          currentLocationPermissions().includes(LocalPermissions.LOCAL_ADMIN) ||
          generalStore.isGlobalAdmin(),
      ),
    };
  }),
  withMethods(
    (store, generalStore = inject(GeneralStore), locationsService = inject(LocationService)) => ({
      deleteLocation(locationID: number) {
        return locationsService.deleteLocation(locationID).pipe(
          tapResponse({
            next: () => {
              patchState(store, removeEntity(locationID));
              this.getEntityData();
              generalStore.getCurrentUser(true);
            },
            error: (error: { message: string }) => setError(error.message),
          }),
        );
      },
      clearCurrentLocation() {
        patchState(store, {
          currentLocation: null,
          currentLocationId: null,
        });
      },
      updateLocationOrder(locations: LocationOrder[]) {
        return locationsService.updateLocationOrder(locations).pipe(
          tapResponse({
            next: () => {
              this.getEntityData();
              generalStore.getCurrentUser(true);
            },
            error: (error: { message: string }) => setError(error.message),
          }),
        );
      },
      getEntityData(params?: ApiRequestParams): void {
        setPending();
        locationsService
          .getLocations(params)
          .pipe(
            tapResponse({
              next: res => {
                let locations: LocationPreview[] = [];
                if (isArray(res)) {
                  locations = res;
                }
                patchState(store, setAllEntities(locations));
                patchState(store, setComplete());
              },
              error: (error: { message: string }) => setError(error.message),
            }),
          )
          .subscribe();
      },
      setError(error: string) {
        patchState(store, setError(error));
      },
      setCurrentLocationId(currentLocationId: number) {
        const currentLocation = store
          .entities()
          .find(location => location.id === currentLocationId);
        patchState(store, {
          currentLocation: currentLocation,
          currentLocationId,
        });
      },
      setPreviousLocation(location: PreviousLocation) {
        patchState(store, {
          previousLocationId: location.previousLocationId,
          previousLocationName: location.previousLocationName,
          previousLocationUrl: location.previousLocationUrl,
        });
      },
    }),
  ),
);
